import { Injectable, NgZone, OnDestroy } from '@angular/core';
import { environment } from 'src/environments/environment';
import Echo from 'laravel-echo';
import Pusher from 'pusher-js';
import { BehaviorSubject, Observable, Subject } from 'rxjs';
import { filter, take, timeout } from 'rxjs/operators';

type LaravelEcho = Echo<'reverb'>;

declare global {
  interface Window {
    Pusher: typeof Pusher;
    Echo: LaravelEcho;
  }
}

type ConnectionState = 'connected' | 'disconnected' | 'connecting';

/** Delays and timeouts (ms) – centralised for production tuning */
const WS = {
  CONNECTION_TIMEOUT_MS: 10000,
  RESUBSCRIBE_AFTER_RECONNECT_MS: 1200,
  INITIAL_RETRY_DELAY_MS: 1000,
  RECONNECT_DELAY_MS: 3000,
  MAX_RECONNECT_ATTEMPTS: 5,
} as const;

@Injectable({
  providedIn: 'root'
})
export class WebSocketService implements OnDestroy {
  private echo: LaravelEcho | null = null;
  private connectionStatus$ = new BehaviorSubject<ConnectionState>('disconnected');
  private messageSubject$ = new Subject<any>();
  private userRemovedSubject$ = new Subject<any>();
  private pollUpdatedSubject$ = new Subject<any>();
  private notificationSubject$ = new Subject<any>();
  private notificationUpdatedSubject$ = new Subject<any>();
  private notificationChannel: any = null;
  private currentNotificationUserId: string | number | null = null;
  private tokenAtInit: string | null = null;
  private reconnectAttempts = 0;
  private reconnectTimer: ReturnType<typeof setTimeout> | null = null;
  private presentMembers: Array<{ id: number | string }> = [];
  private currentPresenceChatId: string | null = null;

  constructor(private readonly ngZone: NgZone) {
    if (this.isBrowser()) {
      this.initializeEcho();
    }
  }

  /** Pusher/Echo callbacks run outside Angular zone; re-enter so UI updates immediately. */
  private emitInZone<T>(subject: Subject<T>, value: T): void {
    this.ngZone.run(() => subject.next(value));
  }

  private isBrowser(): boolean {
    return typeof window !== 'undefined' && typeof window.localStorage !== 'undefined';
  }

  private initializeEcho(): void {
    if (!this.isBrowser()) return;
    if (typeof window !== 'undefined' && !window.Pusher) {
      window.Pusher = Pusher;
    }

    const token = localStorage.getItem('token');
    this.tokenAtInit = token;

    const reverbConfig = environment.reverb;
    const reverbHost = reverbConfig?.host;
    const reverbPort = reverbConfig?.port;
    const reverbScheme = reverbConfig?.scheme || 'http';
    // Use forceTLS from config if provided, otherwise calculate from scheme
    const forceTls = reverbConfig?.forceTLS !== undefined 
      ? reverbConfig.forceTLS 
      : reverbScheme === 'https';
    
    // For localhost/http, prioritize ws over wss
    const enabledTransports = reverbScheme === 'http' 
      ? ['ws'] as any  // Only use ws for http
      : (reverbConfig?.enabledTransports as any) || ['ws', 'wss'];

    try {
      this.echo = new Echo({
        broadcaster: 'reverb',
        key: reverbConfig?.appKey || '',
        wsHost: reverbHost,
        wsPort: reverbPort,
        wssPort: reverbPort,
        forceTLS: forceTls,
        enabledTransports: enabledTransports,
        authEndpoint: reverbConfig?.authEndpoint || '/broadcasting/auth',
        auth: {
          headers: {
            Authorization: token ? `Bearer ${token}` : '',
            Accept: 'application/json',
          },
        },
      });

      this.bindConnectionEvents();

      if (typeof window !== 'undefined' && this.echo) {
        window.Echo = this.echo;
      }
    } catch (error) {
      console.error('Error initializing Echo:', error);
      this.connectionStatus$.next('disconnected');
    }
  }

  private bindConnectionEvents(): void {
    if (!this.echo) {
      return;
    }

    const connector = (this.echo as any).connector;
    const connection = connector?.pusher?.connection;
    if (!connection) {
      return;
    }

    connection.bind('connected', () => {
      this.connectionStatus$.next('connected');
      this.reconnectAttempts = 0; // Reset reconnect attempts on successful connection
      this.clearReconnectTimer();
    });

    connection.bind('disconnected', () => {
      this.connectionStatus$.next('disconnected');
      this.attemptReconnect();
    });

    connection.bind('connecting', () => {
      this.connectionStatus$.next('connecting');
    });

    connection.bind('error', (error: any) => {
      console.error('WebSocket error:', error);
      
      // Check if it's a connection refused error (server not running)
      if (error?.error?.data?.code === 1006 || error?.type === 'WebSocketError') {
        console.error('Reverb server may not be running. Please start it with: php artisan reverb:start');
      }
      
      this.connectionStatus$.next('disconnected');
      this.attemptReconnect();
    });

    connection.bind('unavailable', () => {
      console.error('WebSocket server unavailable');
      this.connectionStatus$.next('disconnected');
      this.attemptReconnect();
    });
  }

  private attemptReconnect(): void {
    if (this.reconnectAttempts >= WS.MAX_RECONNECT_ATTEMPTS) {
      if (!environment.production) {
        console.warn(`[WebSocketService] Max reconnect attempts (${WS.MAX_RECONNECT_ATTEMPTS}) reached.`);
      }
      return;
    }

    if (this.reconnectTimer) return;

    this.reconnectAttempts++;
    const delay = WS.RECONNECT_DELAY_MS * this.reconnectAttempts;

    this.reconnectTimer = setTimeout(() => {
      this.reconnectTimer = null;
      this.reconnect();
    }, delay);
  }

  private clearReconnectTimer(): void {
    if (this.reconnectTimer) {
      clearTimeout(this.reconnectTimer);
      this.reconnectTimer = null;
    }
  }

  subscribeToChat(chatId: string | number): void {
    if (!this.isBrowser()) return;
    if (!this.echo) {
      this.initializeEcho();
      setTimeout(() => this.subscribeToChat(chatId), WS.INITIAL_RETRY_DELAY_MS);
      return;
    }

    const channelName = `chat.${chatId}`;
    
    // Wait for connection before subscribing
    this.waitForConnection().then(() => {
      try {
        const channel = (this.echo as any).private(channelName);

        channel
          .listen('.message.sent', (payload: any) => {
            this.emitInZone(this.messageSubject$, payload);
          })
          .listen('.user.removed', (payload: any) => {
            this.emitInZone(this.userRemovedSubject$, payload);
          })
          .listen('.poll.updated', (payload: any) => {
            this.emitInZone(this.pollUpdatedSubject$, payload);
          })
          .error((error: any) => {
            console.error(`Channel subscription error (${channelName}):`, error);
          });
      } catch (error) {
        console.error(`Error subscribing to channel ${channelName}:`, error);
      }
    }).catch((error) => {
      console.error(`Failed to connect before subscribing to ${channelName}:`, error);
    });
  }

  /**
   * Wait for WebSocket connection to be established
   * @param timeoutMs Maximum time to wait in milliseconds
   */
  private waitForConnection(timeoutMs: number = WS.CONNECTION_TIMEOUT_MS): Promise<void> {
    return new Promise((resolve, reject) => {
      if (this.isConnected()) {
        resolve();
        return;
      }

      const subscription = this.connectionStatus$
        .pipe(
          filter(status => status === 'connected'),
          take(1),
          timeout(timeoutMs)
        )
        .subscribe({
          next: () => {
            subscription.unsubscribe();
            resolve();
          },
          error: (error) => {
            subscription.unsubscribe();
            reject(new Error('Connection timeout'));
          }
        });

      // If currently disconnected, try to reconnect
      if (this.connectionStatus$.value === 'disconnected') {
        this.connectionStatus$.next('connecting');
      }
      });
  }

  unsubscribeFromChat(chatId: string | number): void {
    if (!this.echo) {
      return;
    }
    this.echo.leave(`chat.${chatId}`);
  }

  joinPresenceChannel(chatId: string): void {
    if (!this.isBrowser() || !chatId) {
      return;
    }

    if (this.currentPresenceChatId && this.currentPresenceChatId !== chatId) {
      this.leavePresenceChannel(this.currentPresenceChatId);
    }

    if (this.currentPresenceChatId === chatId) {
      return;
    }

    if (!this.echo) {
      this.initializeEcho();
      setTimeout(() => this.joinPresenceChannel(chatId), WS.INITIAL_RETRY_DELAY_MS);
      return;
    }

    const channelName = `chat-presence.${chatId}`;

    this.waitForConnection().then(() => {
      try {
        (this.echo as any)
          .join(channelName)
          .here((members: Array<{ id: number | string }>) => {
            this.ngZone.run(() => {
              this.presentMembers = members ?? [];
              this.currentPresenceChatId = chatId;
            });
          })
          .joining((member: { id: number | string }) => {
            this.ngZone.run(() => {
              if (!this.presentMembers.some((m) => String(m.id) === String(member.id))) {
                this.presentMembers.push(member);
              }
            });
          })
          .leaving((member: { id: number | string }) => {
            this.ngZone.run(() => {
              this.presentMembers = this.presentMembers.filter(
                (m) => String(m.id) !== String(member.id),
              );
            });
          })
          .error((error: any) => {
            console.error(`Presence channel subscription error (${channelName}):`, error);
          });
      } catch (error) {
        console.error(`Error joining presence channel ${channelName}:`, error);
      }
    }).catch((error) => {
      console.error(`Failed to connect before joining presence channel ${channelName}:`, error);
    });
  }

  leavePresenceChannel(chatId: string): void {
    if (!chatId) {
      return;
    }

    if (this.echo) {
      try {
        this.echo.leave(`chat-presence.${chatId}`);
      } catch (error) {
        console.error(`Error leaving presence channel chat-presence.${chatId}:`, error);
      }
    }

    if (this.currentPresenceChatId === chatId) {
      this.currentPresenceChatId = null;
      this.presentMembers = [];
    }
  }

  isUserPresentInChat(userId: string): boolean {
    return this.presentMembers.some((m) => String(m.id) === String(userId));
  }

  subscribeToNotifications(userId: string | number): void {
    if (!this.isBrowser()) return;

    const token = localStorage.getItem('token');
    if (token && this.echo && this.tokenAtInit !== token) {
      this.reconnect();
      this.waitForConnection(WS.CONNECTION_TIMEOUT_MS)
        .then(() => this.subscribeToNotifications(userId))
        .catch(() => {
          setTimeout(() => this.subscribeToNotifications(userId), WS.RESUBSCRIBE_AFTER_RECONNECT_MS);
        });
      return;
    }

    if (!this.echo) {
      this.initializeEcho();
      setTimeout(() => this.subscribeToNotifications(userId), WS.INITIAL_RETRY_DELAY_MS);
      return;
    }

    // Prevent duplicate subscriptions
    if (this.currentNotificationUserId === userId && this.notificationChannel) {
      return;
    }

    // Unsubscribe from previous user if different
    if (this.currentNotificationUserId && this.currentNotificationUserId !== userId) {
      this.unsubscribeFromNotifications();
    }

    const channelName = `notifications.${userId}`;
    this.currentNotificationUserId = userId;

    // Wait for connection before subscribing
    this.waitForConnection().then(() => {
      try {
        this.notificationChannel = (this.echo as any).private(channelName);

        this.notificationChannel
          .listen('.notification.created', (payload: any) => {
            this.emitInZone(this.notificationSubject$, payload);
          })
          .listen('.notification.updated', (payload: any) => {
            this.emitInZone(this.notificationUpdatedSubject$, payload);
          })
          .error((error: any) => {
            console.error(`Notification channel subscription error (${channelName}):`, error);
          });
      } catch (error) {
        console.error(`Error subscribing to notification channel ${channelName}:`, error);
      }
    }).catch((error) => {
      console.error(`Failed to connect before subscribing to notification channel ${channelName}:`, error);
    });
  }

  unsubscribeFromNotifications(): void {
    if (!this.echo || !this.currentNotificationUserId) {
      return;
    }
    try {
      this.echo.leave(`notifications.${this.currentNotificationUserId}`);
      this.notificationChannel = null;
      this.currentNotificationUserId = null;
    } catch (error) {
      console.error(`Error unsubscribing from notification channel:`, error);
    }
  }

  onMessage(): Observable<any> {
    return this.messageSubject$.asObservable();
  }

  onNotification(): Observable<any> {
    return this.notificationSubject$.asObservable();
  }

  onNotificationUpdated(): Observable<any> {
    return this.notificationUpdatedSubject$.asObservable();
  }

  onUserRemoved(): Observable<any> {
    return this.userRemovedSubject$.asObservable();
  }

  onPollUpdated(): Observable<any> {
    return this.pollUpdatedSubject$.asObservable();
  }

  getConnectionStatus(): Observable<ConnectionState> {
    return this.connectionStatus$.asObservable();
  }

  isConnected(): boolean {
    return this.connectionStatus$.value === 'connected';
  }

  reconnect(): void {
    this.disconnect();
    this.initializeEcho();
  }

  private disconnect(): void {
    this.clearReconnectTimer();
    this.notificationChannel = null;
    this.currentNotificationUserId = null;
    if (this.echo) {
      this.echo.disconnect();
      this.echo = null;
    }
    this.connectionStatus$.next('disconnected');
  }

  /**
   * Ensure connection is ready before performing operations
   * @returns Promise that resolves when connection is ready
   */
  ensureConnection(): Promise<boolean> {
    if (this.isConnected()) {
      return Promise.resolve(true);
    }

    return this.waitForConnection(5000).then(() => true).catch(() => false);
  }

  ngOnDestroy(): void {
    this.clearReconnectTimer();
    this.unsubscribeFromNotifications();
    this.disconnect();
    this.connectionStatus$.complete();
    this.messageSubject$.complete();
    this.userRemovedSubject$.complete();
    this.pollUpdatedSubject$.complete();
    this.notificationSubject$.complete();
    this.notificationUpdatedSubject$.complete();
  }
}

